
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useUsernameValidation } from '@/hooks/useUsernameValidation';
import { Loader2, Check, X } from 'lucide-react';

interface InternalUsernameFieldProps {
  value: string;
  onChange: (value: string) => void;
}

const InternalUsernameField = ({ value, onChange }: InternalUsernameFieldProps) => {
  const [usernameTimeout, setUsernameTimeout] = useState<NodeJS.Timeout | null>(null);
  const { validateUsername, isValidating, validationError, isValid } = useUsernameValidation();

  const handleUsernameChange = (newValue: string) => {
    onChange(newValue);

    // Clear previous timeout
    if (usernameTimeout) {
      clearTimeout(usernameTimeout);
    }

    // Set new timeout for validation
    const timeout = setTimeout(() => {
      if (newValue.trim().length >= 3) {
        validateUsername(newValue);
      }
    }, 500); // Debounce for 500ms

    setUsernameTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (usernameTimeout) {
        clearTimeout(usernameTimeout);
      }
    };
  }, [usernameTimeout]);

  return (
    <div className="space-y-2">
      <Label htmlFor="username" className="text-white">Username</Label>
      <div className="relative">
        <Input
          id="username"
          type="text"
          value={value}
          onChange={(e) => handleUsernameChange(e.target.value)}
          className={`bg-gray-900 border-gray-700 text-white pr-10 ${
            validationError ? 'border-red-500 focus:border-red-500' : ''
          } ${
            isValid && value.length >= 3 && !isValidating ? 'border-green-500' : ''
          }`}
          placeholder="Choose a username"
          required
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isValidating && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          {!isValidating && validationError && <X className="h-4 w-4 text-red-500" />}
          {!isValidating && isValid && value.length >= 3 && <Check className="h-4 w-4 text-green-500" />}
        </div>
      </div>
      {validationError && (
        <p className="text-red-400 text-sm mt-1">{validationError}</p>
      )}
      {!validationError && value.length > 0 && value.length < 3 && (
        <p className="text-gray-400 text-sm mt-1">Username must be at least 3 characters long</p>
      )}
    </div>
  );
};

export default InternalUsernameField;
